import React from "react";
import { Row, Col } from "reactstrap";
import {useTheme} from "../context/ThemeContext";
import {
  Chart as ChartJS,
  CategoryScale, LinearScale, BarElement,
  Title, Tooltip, Legend,
} from "chart.js";
import { Bar } from "react-chartjs-2";

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

/**
 * Shared "what happened next" stats display — the stat-tile row + return
 * histogram used by both the legacy single-horizon ForwardDistribution card
 * and the multi-horizon ForwardCurveFanChart's per-horizon drill-down. Kept
 * in one place so both stay visually and numerically consistent.
 */

export function pct(v: number | null, decimals = 1): string {
  return v !== null ? `${v >= 0 ? "+" : ""}${v.toFixed(decimals)}%` : "—";
}

export function StatTile({ label, value, color, title }: { label: string; value: string; color?: string; title?: string }) {
  return (
    <div className="text-center px-2" title={title}>
      <div style={{ fontSize: 22, fontWeight: 700, color: color ?? "var(--primary)", fontFamily: "var(--font-mono)" }}>{value}</div>
      <div className="text-muted" style={{ fontSize: 11 }}>{label}</div>
    </div>
  );
}

export function buildHistogram(returns: number[], bins = 12): { labels: string[]; data: number[] } {
  if (!returns.length) return { labels: [], data: [] };

  const negs = returns.filter(r => r < 0);
  const poss = returns.filter(r => r >= 0);

  // All on one side — simple uniform binning.
  if (!negs.length || !poss.length) {
    const min = Math.min(...returns);
    const max = Math.max(...returns);
    const binSize = (max - min || 1) / bins;
    const labels: string[] = [];
    const data = new Array(bins).fill(0) as number[];
    for (let i = 0; i < bins; i++) {
      const lo = min + i * binSize;
      labels.push(`${lo >= 0 ? "+" : ""}${lo.toFixed(1)}%`);
    }
    for (const r of returns) {
      data[Math.min(bins - 1, Math.floor((r - min) / binSize))]++;
    }
    return { labels, data };
  }

  // Mixed positive + negative: split bins at EXACTLY zero so a positive return
  // never falls in a red (negative-labeled) bin.  Bins are allocated proportionally
  // to the count of each side to avoid large empty areas on either end.
  const negBins = Math.max(1, Math.round(bins * negs.length / returns.length));
  const posBins = Math.max(1, bins - negBins);

  const minNeg = Math.min(...negs);                   // < 0
  const maxPos = Math.max(...poss);                   // >= 0
  const negBinSize = -minNeg / negBins;               // > 0
  const posBinSize = (maxPos || 1e-9) / posBins;     // > 0; guard when all poss === 0

  const labels: string[] = [];
  const data: number[] = [];

  for (let i = 0; i < negBins; i++) {
    labels.push(`${(minNeg + i * negBinSize).toFixed(1)}%`);
    data.push(0);
  }
  for (let i = 0; i < posBins; i++) {
    labels.push(`+${(i * posBinSize).toFixed(1)}%`);
    data.push(0);
  }
  for (const r of negs) {
    data[Math.min(negBins - 1, Math.floor((r - minNeg) / negBinSize))]++;
  }
  for (const r of poss) {
    data[negBins + Math.min(posBins - 1, Math.floor(r / posBinSize))]++;
  }

  return { labels, data };
}

interface StatRowProps {
  distribution: ForwardDistribution;
  /** Show the Win Rate tile. Default true; the game hides it (see ForwardCurveFanChart). */
  showWinRate?: boolean;
}

/** The stat tiles: Win Rate (optional) / Median Return / Avg Return / 25th-75th pct. */
export function ReturnStatsRow({ distribution, showWinRate = true }: StatRowProps) {
  const { win_rate, median_return, avg_return, p25, p75 } = distribution;
  return (
    <Row className="g-0 mb-3 justify-content-around">
      {showWinRate && (
        <Col xs="auto">
          <StatTile
            label="Win Rate"
            value={win_rate !== null ? `${win_rate.toFixed(1)}%` : "—"}
            color={win_rate !== null && win_rate >= 50 ? "var(--success)" : "var(--error)"}
          />
        </Col>
      )}
      <Col xs="auto">
        <StatTile
          label="Median Return"
          value={pct(median_return)}
          color={median_return !== null && median_return >= 0 ? "var(--success)" : "var(--error)"}
        />
      </Col>
      <Col xs="auto">
        <StatTile
          label="Avg Return"
          value={pct(avg_return)}
          color={avg_return !== null && avg_return >= 0 ? "var(--success)" : "var(--error)"}
          title="The average is pulled up by a few large moves — most individual outcomes cluster nearer the median."
        />
      </Col>
      <Col xs="auto">
        <StatTile label="25th–75th pct" value={`${pct(p25, 0)} / ${pct(p75, 0)}`} color="var(--text-muted)" />
      </Col>
    </Row>
  );
}

interface HistogramProps {
  returns: number[];
  height?: number;
}

/** Bar histogram of forward returns, red below zero / green at-or-above zero. */
export function ReturnHistogram({ returns, height = 140 }: HistogramProps) {
  const {theme} = useTheme();
  const isDark = theme === "dark";
  const { labels, data } = buildHistogram(returns);
  if (!labels.length) return null;
  // Find the first bin that is non-negative.  buildHistogram guarantees all
  // positive-return bins start with "+" and all negative bins start with "-",
  // so checking startsWith("+") is unambiguous (parseFloat("-0.0%") === 0 in JS,
  // which would incorrectly trip the old `>= 0` check near the zero boundary).
  const zeroIdx = (() => {
    const i = labels.findIndex(l => l.startsWith("+"));
    return i === -1 ? labels.length : i;  // -1 means all-negative → all red
  })();

  const redColor  = isDark ? "rgba(248,113,113,0.75)" : "rgba(220,38,38,0.55)";
  const greenColor = isDark ? "rgba(52,211,153,0.75)"  : "rgba(5,150,105,0.55)";
  const tickColor = isDark ? "#94A3B8" : "#64748B";
  const gridColor = isDark ? "rgba(71,85,105,0.5)" : "rgba(203,213,225,0.5)";

  const chartData = {
    labels,
    datasets: [{
      label: "# of Analogs",
      data,
      backgroundColor: labels.map((_, i) => i < zeroIdx ? redColor : greenColor),
      borderRadius: 3,
      // Capped so bars stay bar-shaped (taller than wide) instead of turning
      // into short, fat blocks when there are only a handful of bins to fill
      // the available width.
      maxBarThickness: 32,
    }],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          title: (items: { label: string }[]) => `Return: ${items[0].label}`,
          label: (item: { raw: unknown }) => {
            const n = item.raw as number;
            return `${n} analog${n !== 1 ? "s" : ""}`;
          },
        },
      },
    },
    scales: {
      x: { ticks: { color: tickColor as string, font: { size: 10 } }, grid: { display: false } },
      y: { ticks: { color: tickColor as string, precision: 0 }, grid: { color: gridColor as string } },
    },
  };

  return (
    <div style={{ position: "relative", height, width: "100%", maxWidth: 640 }}>
      <Bar data={chartData} options={chartOptions} />
    </div>
  );
}
